import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  useTheme,
  Button,
  Chip,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Flag as FlagIcon,
  School,
  WorkspacePremium,
  AccessTime,
  Business as BusinessIcon,
  ArrowForwardIos,
} from '@mui/icons-material';
import { businessCourseService } from '@/services/business/businessCourseService';
import { getFileUrl } from '@/utils/businessHelpers';
import { slateJsonToText } from '@/utils/slateSerializer';
import { toast } from 'react-hot-toast';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

interface CourseReport {
  _id: string;
  businessCourseId: any;
  reportedBy: { _id: string; name?: string; email?: string };
  reason: string;
  createdAt: string;
  updatedAt: string;
}

const BusinessCourseReportsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [reports, setReports] = useState<CourseReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  const fetchReports = async (pageNum: number, append = false) => {
    setIsLoading(true);
    try {
      const response: any = await businessCourseService.getBusinessCourseReports(pageNum, 10);
      if (response?.success) {
        const fetched: CourseReport[] = response.data?.reports || [];
        setReports((prev) => (append ? [...prev, ...fetched] : fetched));
        setHasMore(Boolean(response.data?.pagination?.hasNext));
        setTotalItems(response.data?.pagination?.totalItems ?? response.data?.pagination?.total ?? fetched.length);
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to fetch course reports');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReports(1);
  }, []);

  const handleRefresh = () => {
    setPage(1);
    fetchReports(1);
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchReports(nextPage, true);
  };

  const renderRow = (report: CourseReport) => {
    const course = report.businessCourseId;
    const businessInfo = course?.businessId && typeof course.businessId === 'object' ? course.businessId : null;
    const courseBusinessId = typeof course?.businessId === 'string' ? course?.businessId : course?.businessId?._id;

    const goToCourse = () => {
      if (course && courseBusinessId) {
        navigate(`/businesses/${courseBusinessId}/courses/${course._id}`);
      }
    };

    if (!course) {
      return (
        <Paper
          key={report._id}
          variant="outlined"
          sx={{ p: 2, mb: 1.5, borderRadius: 2, borderColor: theme.palette.divider }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <FlagIcon color="error" fontSize="small" />
            <Typography variant="body2" fontWeight={600}>
              {report.reportedBy?.name || 'Unknown'}
            </Typography>
            <Chip label={report.reason} size="small" color="error" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {moment(report.createdAt).fromNow()}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            Reported course has been deleted or is unavailable.
          </Typography>
        </Paper>
      );
    }

    const description = course.description ? slateJsonToText(course.description) : '';

    return (
      <Paper
        key={report._id}
        variant="outlined"
        onClick={goToCourse}
        sx={{
          mb: 1.5,
          borderRadius: 2,
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'border-color 0.2s, box-shadow 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: theme.shadows[2],
          },
        }}
      >
        {/* Report bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            bgcolor: theme.palette.mode === 'light' ? 'error.50' : 'rgba(244,67,54,0.08)',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <FlagIcon color="error" sx={{ fontSize: 18 }} />
          <Typography variant="caption" fontWeight={600} color="error.dark" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {report.reason}
          </Typography>
          <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.disabled' }} />
          <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
            {report.reportedBy?.name || 'Unknown'}
            {report.reportedBy?.email ? ` · ${report.reportedBy.email}` : ''}
          </Typography>
          <Tooltip title={moment(report.createdAt).format('DD MMM YYYY, h:mm A')}>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', flexShrink: 0 }}>
              {moment(report.createdAt).fromNow()}
            </Typography>
          </Tooltip>
        </Box>

        {/* Course row */}
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {/* Thumbnail */}
          <Box
            sx={{
              width: 96,
              height: 72,
              flexShrink: 0,
              borderRadius: 1.5,
              overflow: 'hidden',
              bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {course.courseThumbnail?.url ? (
              <Box
                component="img"
                src={course.courseThumbnail.url}
                alt={course.courseTitle}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <School sx={{ fontSize: 32, color: 'text.disabled' }} />
            )}
          </Box>

          {/* Body */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {course.courseTitle}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              {course.level && (
                <Chip label={course.level} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
              )}
              {course.courseType && (
                <Chip
                  label={course.courseType}
                  size="small"
                  color={course.courseType === 'Paid' ? 'secondary' : 'success'}
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              )}
              {course.certificationAvailable && (
                <Chip
                  icon={<WorkspacePremium sx={{ fontSize: 11 }} />}
                  label="Certified"
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              )}
              {course.duration && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, color: 'text.secondary' }}>
                  <AccessTime sx={{ fontSize: 12 }} />
                  <Typography variant="caption">{course.duration}</Typography>
                </Box>
              )}
              {course.numberOfModules ? (
                <Typography variant="caption" color="text.secondary">
                  · {course.numberOfModules} modules
                </Typography>
              ) : null}
              <Box sx={{ flex: 1 }} />
              <Typography variant="body2" color="primary" fontWeight={700}>
                {course.courseType === 'Free'
                  ? 'Free'
                  : course.price !== undefined && course.price > 0
                  ? `₹${Number(course.price).toLocaleString('en-IN')}`
                  : 'Price on Request'}
              </Typography>
            </Box>

            {description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.75,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {description}
              </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              {businessInfo && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <Avatar
                    src={businessInfo.logoId ? getFileUrl(businessInfo.logoId) : undefined}
                    sx={{ width: 20, height: 20, fontSize: 11 }}
                  >
                    {businessInfo.businessName?.charAt(0) || <BusinessIcon sx={{ fontSize: 12 }} />}
                  </Avatar>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {businessInfo.businessName}
                  </Typography>
                </Box>
              )}
              {course.isDeleted && <Chip label="Deleted" size="small" color="error" sx={{ height: 18, fontSize: '0.6rem' }} />}
              {course.isActive === false && <Chip label="Inactive" size="small" sx={{ height: 18, fontSize: '0.6rem' }} />}
            </Box>
          </Box>

          <ArrowForwardIos sx={{ fontSize: 14, color: 'text.disabled', alignSelf: 'center', flexShrink: 0 }} />
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h5" fontWeight={700}>
          Reported Courses
        </Typography>
        {totalItems > 0 && (
          <Chip
            label={`${totalItems} ${totalItems === 1 ? 'report' : 'reports'}`}
            size="small"
            color="error"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={handleRefresh} disabled={isLoading} color="primary">
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {isLoading && reports.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : reports.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <FlagIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            No course reports to review.
          </Typography>
        </Paper>
      ) : (
        <Box>
          {reports.map(renderRow)}

          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={isLoading}
                sx={{ borderRadius: '20px', px: 4, textTransform: 'none', fontWeight: 600 }}
              >
                {isLoading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : 'Load more'}
              </Button>
            </Box>
          )}

          {!hasMore && (
            <Typography variant="caption" color="text.disabled" align="center" sx={{ display: 'block', mt: 2 }}>
              You've reviewed all course reports.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BusinessCourseReportsPage;
